import { useMutation, useQuery } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import type { CheckoutDesign } from 'views/CheckoutDesign/types';

import { EQueryKeys } from '../constants/enums';
import useAxios from '../hooks/useAxios';

import { EApiRoutes, fetchConfig, getApiRouteOrMock } from './api.utils';

export default function useCheckoutDesign(
  currentPublisherId?: string,
  templateId?: string
) {
  const axios = useAxios();
  const queryClient = useQueryClient();

  const getCheckoutDesigns = useQuery({
    ...fetchConfig.list,
    queryKey: [EQueryKeys.CHECKOUT_DESIGNS],
    queryFn: async () => {
      const checkoutDesignRaw = await axios.get(
        getApiRouteOrMock(EApiRoutes.CHECKOUT_DESIGNS),
        {},
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
      return checkoutDesignRaw;
    },
    enabled: !!currentPublisherId
  });

  const getCheckoutDesign = useQuery({
    ...fetchConfig.single,
    queryKey: [`${EQueryKeys.CHECKOUT_DESIGNS}_${templateId}`],
    queryFn: async () => {
      return await axios.get(
        `${getApiRouteOrMock(EApiRoutes.CHECKOUT_DESIGNS)}/${templateId}`,
        {},
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
    },
    enabled: !!currentPublisherId && !!templateId
  });

  const addCheckoutDesign = useMutation(
    async (newCheckoutDesign: CheckoutDesign) => {
      return await axios.post(
        getApiRouteOrMock(EApiRoutes.CHECKOUT_DESIGNS),
        newCheckoutDesign
      );
    },
    {}
  );

  const deleteCheckoutDesign = useMutation(async (templateId: string) => {
    return await axios.del(
      `${getApiRouteOrMock(EApiRoutes.CHECKOUT_DESIGNS)}/${templateId}`
    );
  }, {});

  const updateCheckoutDesign = useMutation(
    async ({
      form,
      templateId
    }: {
      form: Partial<CheckoutDesign>;
      templateId?: string;
    }) => {
      return axios.put(
        `${getApiRouteOrMock(EApiRoutes.CHECKOUT_DESIGNS)}/${templateId}`,
        form
      );
    },
    {
      onSuccess: (data, variables) => {
        queryClient.setQueryData(
          [`${EQueryKeys.CHECKOUT_DESIGNS}_${variables.templateId}`],
          data
        );
      }
    }
  );

  return {
    getCheckoutDesigns,
    getCheckoutDesign,
    addCheckoutDesign,
    deleteCheckoutDesign,
    updateCheckoutDesign
  };
}
